import { useEffect, useState } from 'react';

const SIZES = [5, 10, 25, 0] as const;

function remembered(key: string, fallback: number): number {
  try {
    const saved = Number(localStorage.getItem(`hub.pageSize.${key}`));
    return (SIZES as readonly number[]).includes(saved) ? saved : fallback;
  } catch {
    return fallback;
  }
}

/**
 * A long list in blocks of a few, for the cards that are not tables.
 *
 * The team on a project lists everybody with a role on the company, and the company's people
 * card lists every one of them — a few dozen rows pushing everything else on the page out of
 * sight. This shows a block at a time with the choice of how many, remembered per list in this
 * browser. Tables have the same thing built into `DataTable`; this is for the plain lists.
 *
 * Returns the rows to draw and the control to put under them. The control draws nothing when
 * the whole list fits in the smallest block, because a pager over five rows is noise.
 */
export function usePaged<T>(items: T[], key: string, initial = 10) {
  const [size, setSize] = useState(() => remembered(key, initial));
  const [page, setPage] = useState(0);

  const pages = size === 0 ? 1 : Math.max(1, Math.ceil(items.length / size));
  // A list that shrank under the current page — somebody removed — falls back to its last page.
  useEffect(() => {
    if (page > pages - 1) setPage(pages - 1);
  }, [page, pages]);

  const at = Math.min(page, pages - 1);
  const visible = size === 0 ? items : items.slice(at * size, at * size + size);

  function choose(next: number) {
    setSize(next);
    setPage(0);
    try {
      localStorage.setItem(`hub.pageSize.${key}`, String(next));
    } catch {
      /* private browsing */
    }
  }

  const pager =
    items.length <= SIZES[0] ? null : (
      <div className="list-pager">
        <span className="dim">
          {size === 0 || items.length <= size
            ? `All ${items.length}`
            : `${at * size + 1}–${at * size + visible.length} of ${items.length}`}
        </span>
        <span className="row" style={{ gap: 6 }}>
          {pages > 1 && (
            <>
              <button type="button" className="btn btn-quiet btn-sm" disabled={at === 0} onClick={() => setPage(at - 1)}>
                ‹ Previous
              </button>
              <span className="dim mono" style={{ fontSize: 12 }}>
                {at + 1} / {pages}
              </span>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                disabled={at >= pages - 1}
                onClick={() => setPage(at + 1)}
              >
                Next ›
              </button>
            </>
          )}
          <select
            aria-label="How many to show"
            value={size}
            onChange={(event) => choose(Number(event.target.value))}
            style={{ width: 'auto' }}
          >
            {SIZES.map((option) => (
              <option key={option} value={option}>
                {option === 0 ? 'Show all' : `Show ${option}`}
              </option>
            ))}
          </select>
        </span>
      </div>
    );

  return { visible, pager };
}
